import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TenantService } from './tenant.service';

@Injectable({ providedIn: 'root' })
export class StatisticsService {
  private get base() { return `${this.tenant.apiBaseUrl}/v1/tenant/statistics`; }
  constructor(private http: HttpClient, private tenant: TenantService) {}

  performanceOverview(params?: any): Observable<any> {
    return this.http.get(`${this.base}/performance/overview`, { params });
  }
  exportOverview(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/performance/overview/export`, { params, responseType: 'blob' });
  }
  /**
   * v1.6.1788 — the same export, observing the whole response.
   *
   * `format` is REQUIRED and is `csv` or `pdf`; omitting it is a 422, not a
   * default to CSV.
   *
   * The response is needed rather than just the body because the filename
   * comes from `Content-Disposition` — the contract says to take the server's
   * name and not compose one, and a bare-blob request cannot see the header.
   * On failure the body is JSON rather than a file, which on a blob request
   * arrives as a Blob: read it with `extractBlobError`.
   */
  exportOverviewResponse(params?: any): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.base}/performance/overview/export`, {
      params, responseType: 'blob', observe: 'response',
    });
  }
  revenueBreakdown(params?: any): Observable<any> {
    return this.http.get(`${this.base}/revenue-breakdown`, { params });
  }
  exportRevenue(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/revenue-breakdown/export`, { params, responseType: 'blob' });
  }
  orders(params?: any): Observable<any> {
    return this.http.get(`${this.base}/orders`, { params });
  }
  products(params?: any): Observable<any> {
    return this.http.get(`${this.base}/products`, { params });
  }
  exportProducts(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/products/export`, { params, responseType: 'blob' });
  }
  compareProducts(params?: any): Observable<any> {
    return this.http.get(`${this.base}/products/compare`, { params });
  }

  /**
   * v1.6.1794 — one product's sales day by day (Asana 1219775387050448).
   *
   * `productId` goes in the PATH, so it is interpolated rather than passed as
   * a param; an id that is not digits answers 404, which the screen shows as
   * a "product not found" state rather than a toast.
   *
   * The period is built by `periodParams` in product-sales-by-date.utils —
   * never assembled here — because a preset and dates travelling together is
   * a 422 and that decision belongs in one place.
   */
  productSalesByDate(productId: number | string, params?: any): Observable<any> {
    return this.http.get(`${this.base}/products/${productId}/sales-by-date`, { params });
  }

  /**
   * The same figures as a file.
   *
   * Observed as the whole response for the filename in `Content-Disposition`,
   * exactly as `exportOverviewResponse` above. Note what this being an
   * Authorization-bearing XHR buys: the contract points out that a plain
   * `<a href>` or `window.location` cannot send the token, so a link-based
   * download would answer 401 every time.
   *
   * A failure arrives as JSON inside a Blob — read it with `extractBlobError`.
   */
  exportProductSalesByDate(
    productId: number | string,
    params?: any,
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.base}/products/${productId}/sales-by-date/export`, {
      params, responseType: 'blob', observe: 'response',
    });
  }
  categories(params?: any): Observable<any> {
    return this.http.get(`${this.base}/categories`, { params });
  }
  exportCategories(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/categories/export`, { params, responseType: 'blob' });
  }
  compareCategories(params?: any): Observable<any> {
    return this.http.get(`${this.base}/categories/compare`, { params });
  }
  customers(params?: any): Observable<any> {
    return this.http.get(`${this.base}/customers`, { params });
  }
  /**
   * v1.6.1685 — `GET /v1/tenant/statistics/customers/export` was DELETED by
   * Asana 1219162961227281 and answers 404. The method is removed rather than
   * left in place: a dead service method is an invitation to call it, and the
   * ticket's own acceptance criteria require that no request anywhere in the
   * app still reaches that URL.
   *
   * The replacement is the generic data-transfer subsystem —
   * `DataTransferService.startExportObserved('customer_statistics', body)` —
   * which handles both the inline-file and queued-run outcomes. See
   * `statistics-export.utils.ts`.
   */
  /**
   * v1.6.1686 — Asana 1219300111973811: Reward Points by Date.
   *
   * NET per-day figures, as against `GET /statistics/rewards`, which is GROSS.
   * The two are expected to differ and both are correct — a returned point
   * still counts as earned in the summary. `rewards-by-date.utils.ts` carries
   * the rest of the rules.
   *
   * Params are built by `buildRewardsByDateQuery`, which enforces the
   * both-or-neither date rule client-side: one date alone is a 422, and so is
   * an empty string, so a cleared picker must drop the key entirely.
   */
  rewardsByDate(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.base}/rewards/by-date`, { params: params ?? {} });
  }

  customerOrders(customerId: number, params?: any): Observable<any> {
    return this.http.get(`${this.base}/customers/${customerId}/orders`, { params });
  }
  coupons(params?: any): Observable<any> {
    return this.http.get(`${this.base}/coupons`, { params });
  }
  couponDiscountSummary(params?: any): Observable<any> {
    return this.http.get(`${this.base}/coupons/discount-summary`, { params });
  }
  couponDiscountTypeBreakdown(params?: any): Observable<any> {
    return this.http.get(`${this.base}/coupons/discount-type-breakdown`, { params });
  }
  couponUsage(params?: any): Observable<any> {
    return this.http.get(`${this.base}/coupons/usage`, { params });
  }
  exportCouponUsage(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/coupons/usage/export`, { params, responseType: 'blob' });
  }
  mealPlans(params?: any): Observable<any> {
    return this.http.get(`${this.base}/meal-plans`, { params });
  }
  mealPlanDetail(id: number, params?: any): Observable<any> {
    return this.http.get(`${this.base}/meal-plans/${id}`, { params });
  }
  compareMealPlans(params?: any): Observable<any> {
    return this.http.get(`${this.base}/meal-plans/compare`, { params });
  }
  exportMealPlans(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/meal-plans/export`, { params, responseType: 'blob' });
  }
  giftCards(params?: any): Observable<any> {
    return this.http.get(`${this.base}/gift-cards`, { params });
  }
  referrals(params?: any): Observable<any> {
    return this.http.get(`${this.base}/referrals`, { params });
  }
  rewards(params?: any): Observable<any> {
    return this.http.get(`${this.base}/rewards`, { params });
  }
  /**
   * v1.6.1268 — GET /statistics/abandoned-cart. Same store_id + date range params as
   * the other statistics screens. With no date range the endpoint returns the
   * campaign's whole history, which is what merchants want on first load.
   */
  abandonedCart(params?: any): Observable<any> {
    return this.http.get(`${this.base}/abandoned-cart`, { params });
  }

  // ── v1.6.1462 — Tax Report (Asana 1217711797729134)
  //
  // PERIOD BASIS, and the reason it is worth a comment: the window filters on
  // the SALE date (orders.date_created), NOT the delivery date and NOT
  // date_paid. The spec is explicit that date_paid is unusable because checkout
  // sets it to the delivery date on one branch. So these params mean something
  // different from the coupon report's payment-date filter (HANDOFF §3.5), and
  // the two reports will legitimately disagree.
  //
  // Default window when start_date/end_date are omitted is 6 days before today
  // through today — i.e. 7 days, not the 30 the other analytics screens preset.

  /** One row per tax code, paginated. `data.tax_codes` + `data.meta`. */
  taxCodes(params?: any): Observable<any> {
    return this.http.get(`${this.base}/tax`, { params });
  }

  /**
   * The same rows, unpaginated, streamed as CSV.
   * Bounded by the configured unpaginated limit, which REJECTS an over-limit
   * request rather than truncating — so callers must surface the error body.
   * See `extractBlobError`: on a blob request the error body is itself a Blob.
   */
  exportTaxCodes(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/tax/export`, { params, responseType: 'blob' });
  }

  /** KPI cards + timeline + optional equal-length comparison window. */
  taxOverview(params?: any): Observable<any> {
    return this.http.get(`${this.base}/tax/overview`, { params });
  }

  // ── v1.6.1806 — Tax by city (Asana 1219812168915091) ───────────────────
  //
  // Same `statistic.viewAny` permission as the rest of the tax report, and
  // the same orders counted: "with no filters, summary.total_tax and
  // summary.orders equal the overview endpoint's kpis.total_tax and
  // kpis.orders for the same period."
  //
  // `cities[]` is repeated-key WITH the brackets. Angular's HttpParams repeats
  // the key exactly as given, so `buildCityQuery` must name it `cities[]` —
  // a bare `cities` key reaches PHP as a single string and is a 422.

  /** One row per city. `data.summary` + `data.cities` + `data.meta`. */
  taxByCity(params?: any): Observable<any> {
    return this.http.get(`${this.base}/tax/cities`, { params });
  }

  /**
   * The city list for the filter picker, merged exactly like the table so
   * each key selects exactly one table row.
   *
   * With no period it covers ALL TIME, which is the right default for a
   * picker the operator may open before choosing a period — but the page
   * passes its own period so the list only offers cities with data in it.
   */
  taxCityOptions(params?: any): Observable<any> {
    return this.http.get(`${this.base}/tax/cities/filter-options`, { params });
  }

  // ── v1.6.1462 — Meal Reorder Analytics (Asana 1217554546739590)
  //
  // Five read-only endpoints behind the same `statistic.viewAny` permission as
  // the rest of this section, all taking the SAME filter set — the query string
  // is built once by `buildReorderQuery` and reused across every screen.
  //
  // The date range does NOT mean what it usually means here. It selects which
  // purchases ANCHOR a customer to a meal; reorders are then observed after it,
  // INCLUDING PAST end_date. That is deliberate, so a recent purchase is not
  // scored as a failure for not having been re-bought yet, and `meta` says so
  // on every response via `anchor_scope` and
  // `reorder_window_extends_beyond_end_date`.

  /** The whole dashboard in ONE request — KPIs, four rankings, quadrant counts, weekly trend. */
  mealReorderOverview(params?: any): Observable<any> {
    return this.http.get(`${this.base}/meals/reorder/overview`, { params });
  }

  /** The per-meal performance table. Paginated; `data.meals` + `data.meta`. */
  mealReorderPerformance(params?: any): Observable<any> {
    return this.http.get(`${this.base}/meals/reorder`, { params });
  }

  /**
   * The same rows as CSV, unpaginated. A STREAM, not JSON — never parsed.
   * Rejects a request above the unpaginated ceiling rather than trimming
   * silently, so the error body has to be readable: see `extractBlobError`.
   */
  exportMealReorder(params?: any): Observable<Blob> {
    return this.http.get(`${this.base}/meals/reorder/export`, { params, responseType: 'blob' });
  }

  /**
   * One meal's drill-down: charts, benchmarks, segments.
   *
   * 404 = no meal with that id. 422 = the meal EXISTS but sold nothing in the
   * range, and the message names the meal and the range — show it as received
   * rather than rendering a page of zeroes, which would read as failure.
   * A retired meal still resolves; its history is the point of the screen.
   */
  mealReorderDetail(productId: number, params?: any): Observable<any> {
    return this.http.get(`${this.base}/meals/${productId}/reorder`, { params });
  }

  /**
   * That meal's cohort table. SEPARATE from the detail call on purpose: the
   * week/month toggle re-fetches only this, leaving the detail page's charts,
   * benchmarks and segments untouched.
   */
  mealReorderCohorts(productId: number, params?: any): Observable<any> {
    return this.http.get(`${this.base}/meals/${productId}/reorder/cohorts`, { params });
  }

  /**
   * v1.6.1585 — GET /statistics/orders/origins (Asana 1218629057611189).
   *
   * Orders broken down by marketing source. Same three params as every other
   * card here, and UNBOUNDED by default — matching /statistics/orders, so the
   * two answer the same window for the same inputs.
   */
  orderOrigins(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.base}/orders/origins`, { params: params ?? {} });
  }
}
